import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

export default function Home() {
  const [posts, setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/articles.json`).catch(() => ({ data: { articles: [] } })),
      axios.get(`${API_URL}/api/press-releases.json`).catch(() => ({ data: { pressReleases: [] } })),
    ]).then(([artRes, prRes]) => {
      const articles = (artRes.data.articles || []).map((a) => ({
        id: a.id,
        type: 'article',
        title: a.title,
        excerpt: a.content || '',
        category: null,
        authorName: a.authorName,
        createdAt: a.createdAt,
        link: `/article/${a.id}`,
      }));

      const pressReleases = (prRes.data.pressReleases || []).map((pr) => ({
        id: pr.id,
        type: 'press-release',
        title: pr.headline,
        excerpt: pr.summary || pr.body || '',
        category: pr.category,
        authorName: pr.authorName,
        createdAt: pr.createdAt,
        link: `/press-release/${pr.id}`,
      }));

      const merged = [...articles, ...pressReleases].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      setPosts(merged);
      setLoading(false);
    });
  }, []);

  async function handleLogout() {
    try { logout(); navigate('/login'); } catch { setError('Failed to log out.'); }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  }

  return (
    <div style={s.container}>
      <header style={s.header}>
        <div style={s.logoWrap}>
          <span style={s.logoEmoji}>🍇</span>
          <h1 style={s.logo}>Coneberry</h1>
        </div>
        <nav style={s.nav}>
          {currentUser ? (
            <>
              <span style={s.userEmail}>{currentUser.displayName || currentUser.email}</span>
              <Link to="/dashboard" style={s.navBtn}>Dashboard</Link>
              <button onClick={handleLogout} style={s.navBtnOutline}>Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" style={s.navBtnOutline}>Login</Link>
              <Link to="/signup" style={{ ...s.navBtn, ...s.navBtnPrimary }}>Sign Up</Link>
            </>
          )}
        </nav>
      </header>

      <main style={s.main}>
        <h2 style={s.pageTitle}>Latest News</h2>

        {error && <div style={s.error}>{error}</div>}

        {loading ? (
          <div style={s.loading}>Loading...</div>
        ) : posts.length === 0 ? (
          <div style={s.empty}>
            <p>Nothing published yet.</p>
            {currentUser && <Link to="/dashboard/press-release/add" style={{ ...s.navBtn, ...s.navBtnPrimary }}>Create the first post</Link>}
          </div>
        ) : (
          <div style={s.grid}>
            {posts.map((post) => (
              <Link key={`${post.type}-${post.id}`} to={post.link} style={s.cardLink}>
                <article style={s.card}>
                  <div style={s.cardTop}>
                    <span style={post.type === 'press-release' ? s.badgePR : s.badgeArticle}>
                      {post.type === 'press-release' ? 'Press Release' : 'Article'}
                    </span>
                    {post.category && <span style={s.badgeCategory}>{post.category}</span>}
                    <span style={s.date}>{formatDate(post.createdAt)}</span>
                  </div>
                  <h3 style={s.cardTitle}>{post.title}</h3>
                  <p style={s.cardMeta}>By <strong>{post.authorName}</strong></p>
                  <p style={s.cardExcerpt}>
                    {post.excerpt.length > 200 ? post.excerpt.substring(0, 200) + '...' : post.excerpt}
                  </p>
                  <span style={s.readMore}>Read more →</span>
                </article>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  container: { fontFamily: "'Inter', system-ui, sans-serif", minHeight: '100vh', background: '#f8f9fa' },
  header: {
    background: '#1c1917', color: '#fff',
    padding: '0 2rem',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    height: '64px',
  },
  logoWrap: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  logoEmoji: { fontSize: '1.4rem' },
  logo: { margin: 0, fontSize: '1.4rem', fontWeight: 800, color: '#f97316' },
  nav: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  userEmail: { color: '#ccc', fontSize: '0.85rem' },
  navBtn: {
    padding: '0.4rem 1rem', borderRadius: '4px',
    background: 'transparent', border: '1px solid #f97316',
    color: '#f97316', cursor: 'pointer',
    textDecoration: 'none', fontSize: '0.875rem',
  },
  navBtnOutline: {
    padding: '0.4rem 1rem', borderRadius: '4px',
    background: 'transparent', border: '1px solid #666',
    color: '#ccc', cursor: 'pointer',
    fontSize: '0.875rem', textDecoration: 'none',
  },
  navBtnPrimary: { background: '#f97316', color: '#fff', border: '1px solid #f97316' },
  main: { maxWidth: '900px', margin: '0 auto', padding: '2rem 1rem' },
  pageTitle: { fontSize: '1.8rem', marginBottom: '1.5rem', color: '#1c1917', fontWeight: 700 },
  error: { background: '#fee', border: '1px solid #fcc', padding: '1rem', borderRadius: '6px', marginBottom: '1rem', color: '#c00' },
  loading: { textAlign: 'center', color: '#666', padding: '3rem' },
  empty: { textAlign: 'center', color: '#666', padding: '3rem' },
  grid: { display: 'grid', gap: '1.25rem' },
  cardLink: { textDecoration: 'none', color: 'inherit', display: 'block' },
  card: {
    background: '#fff', borderRadius: '8px',
    padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
    borderLeft: '4px solid #f97316',
    transition: 'box-shadow 0.15s',
  },
  cardTop: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' },
  badgePR: {
    background: '#f97316', color: '#fff',
    fontSize: '0.68rem', fontWeight: 700,
    padding: '0.15rem 0.55rem', borderRadius: '20px',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  badgeArticle: {
    background: '#1c1917', color: '#fff',
    fontSize: '0.68rem', fontWeight: 700,
    padding: '0.15rem 0.55rem', borderRadius: '20px',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  badgeCategory: {
    background: '#f0f0f0', color: '#555',
    fontSize: '0.68rem', fontWeight: 600,
    padding: '0.15rem 0.55rem', borderRadius: '20px',
  },
  date: { color: '#aaa', fontSize: '0.8rem', marginLeft: 'auto' },
  cardTitle: { margin: '0 0 0.3rem', fontSize: '1.15rem', color: '#1c1917', lineHeight: 1.3, fontWeight: 700 },
  cardMeta: { margin: '0 0 0.75rem', color: '#888', fontSize: '0.82rem' },
  cardExcerpt: { margin: '0 0 1rem', color: '#555', lineHeight: 1.6, fontSize: '0.92rem' },
  readMore: { color: '#f97316', fontSize: '0.85rem', fontWeight: 600 },
};
